/**
 * TemplatrContextStorage.ts - Store user navigation contexts in Templatr
 * 
 * CHANGE HISTORY:
 * - 251122_1516 danielbeer: Created for Phase 4 integration
 */

import type { NavigationContext } from '../components/ContextSelector';

export class TemplatrContextStorage {
  private templatr: any;
  private templateType = 'navigation-context';

  constructor(templatr?: any) {
    this.templatr = templatr;
  }

  /**
   * Initialize Templatr
   */
  async initialize(): Promise<void> {
    if (!this.templatr) {
      const { getTemplatr } = await import('@opcentral/templatr');
      this.templatr = getTemplatr();
    }
  }

  /**
   * Get all user contexts
   */
  async getUserContexts(): Promise<NavigationContext[]> {
    await this.initialize();
    
    try {
      const templates = this.templatr.getTemplatesByType(this.templateType);
      return templates.map((t: any) => ({
        ...t.data,
        type: 'user'
      }));
    } catch (e) {
      console.warn('[TemplatrContextStorage] Failed to get contexts:', e);
      return [];
    }
  }

  /**
   * Save a user context
   */
  async saveUserContext(context: NavigationContext): Promise<void> {
    await this.initialize();
    
    const template = this.templatr.createTemplate(this.templateType, {
      ...context,
      type: 'user'
    });
    
    this.templatr.saveTemplate(template);
  }

  /**
   * Update a user context
   */
  async updateUserContext(contextId: string, updates: Partial<NavigationContext>): Promise<void> {
    await this.initialize();
    
    const template = this.templatr.getTemplate(contextId);
    if (template) {
      template.data = {
        ...template.data,
        ...updates
      };
      this.templatr.saveTemplate(template);
    }
  }

  /**
   * Delete a user context
   */
  async deleteUserContext(contextId: string): Promise<void> {
    await this.initialize();
    
    this.templatr.deleteTemplate(contextId);
  }

  /**
   * Get a specific context by ID
   */
  async getContext(contextId: string): Promise<NavigationContext | null> {
    await this.initialize();
    
    try {
      const template = this.templatr.getTemplate(contextId);
      return template ? { ...template.data, type: 'user' } : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Duplicate a context (for creating user copy of fixed context)
   */
  async duplicateContext(sourceContext: NavigationContext, newName: string): Promise<NavigationContext> {
    const newContext: NavigationContext = {
      ...sourceContext,
      id: `user-${Date.now()}`,
      name: newName,
      type: 'user'
    };
    
    await this.saveUserContext(newContext);
    return newContext;
  }
}

// Singleton
let storageInstance: TemplatrContextStorage | null = null;

export function getTemplatrContextStorage(): TemplatrContextStorage {
  if (!storageInstance) {
    storageInstance = new TemplatrContextStorage();
  }
  return storageInstance;
}
